import React, { useEffect, useState, useMemo, useCallback } from "react";
import * as actions from "../../../redux/actions/artistManager";
import { useSelector, useDispatch } from "react-redux";
import Calendar from "../../../components/Calendar";
import TaskPlanner from "./TaskPlanner/TaskPlanner";
import RevenueEstimator from "./RevenueEstimator";
import { convertDataFormat } from "../../../helper/dataFormat";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LogRevenue } from "./LogRevenue";
import { LogExpenses } from "./LogExpenses";
import { ROICalculator } from "./ROICalculator";
import { ForecastEngine } from "./ForecastEngine";
import { useLocation, useNavigate } from "react-router-dom";
import ChatInput from "../AmplitudeAI/Chat/ChatInput";
import { sendMessage } from "../../../redux/actions";
import { showToast } from "../../../helper/ShowToast";
import { PricingPlanModal } from "../../../components/PricingPlanModal";
import { CreditPlanModal } from "../../../components/CreditPlanModal";
import AppTour from "../../../components/AppTour/AppTour";
import {
    hasArtistLinked,
    hasFinancialPlannerDspAccess,
} from "../../../helper/access";
import { financeHubTourSteps } from "../../../components/AppTour/ModuleTourSteps";

export const ReleaseAndFinancialPlanner = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const { state, hash } = useLocation()
    const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);
    const [showCreditPlanModal, setShowCreditPlanModal] = useState(false);
    const [runTour, setRunTour] = useState(false)
    const [loading, setLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [viewMode, setViewMode] = useState("calendar"); // "month" or "year"
    const { showLoad } = useSelector((state) => state?.amplitudeReducer);
    const billing = useSelector((state) => state.billing);
    const { hasActiveSubscription, creditDetails } = billing;
    const access = useSelector((state) => state.auth.access);
    const userDetails = useSelector((state) => state.auth.userDetails);
    const artistManagerReducer = useSelector((state) => state.artistManager);
    const events = convertDataFormat(artistManagerReducer?.tasks?.data || [])

    const needsLinkArtist = !hasArtistLinked(access, userDetails);
    const dspEstimatesEnabled = useMemo(
        () => hasFinancialPlannerDspAccess(access, userDetails, billing),
        [access, userDetails, billing]
    );

    const handleLinkArtistClick = useCallback(() => {
        showToast("info", "Link your artist profile to unlock financial planner insights.");
        navigate("/link-accounts");
    }, [navigate]);

    const handleSubscribeClick = useCallback(() => {
        showToast("info", "Please subscribe to unlock financial planner insights.");
        setShowPricingPlanModal(true);
    }, []);

    const dspLockProps = useMemo(
        () => ({
            dspEstimatesEnabled,
            needsLinkArtist,
            onLinkArtist: handleLinkArtistClick,
            onSubscribe: handleSubscribeClick,
        }),
        [dspEstimatesEnabled, needsLinkArtist, handleLinkArtistClick, handleSubscribeClick]
    );

    useEffect(() => {
        if (state?.scrollToCenter) {
            const y = (document.documentElement.scrollHeight - window.innerHeight) / 2;

            window.scrollTo({
                top: y,
                behavior: "smooth"
            });
        }
    }, [state]);

    useEffect(() => {
        if(hash === "#start-tour"){
            setRunTour(true)
        }
    }, [hash])

    useEffect(() => {
        dispatch(actions.fetchAllTasks(toast, setLoading))
    }, [dispatch, access?.artist?.activeArtistProfileId])

    const handleCalendarChange = (value) => { }

    const handleSend = (input) => {
        if (!hasActiveSubscription) {
            showToast("info", "Please subscribe to continue using Amplitude AI.");
            setShowPricingPlanModal(true);   // Open subscription modal
            return;
        }
        //BLOCK if no credits
        if (!creditDetails?.data || creditDetails?.data?.remaining <= 0) {
            showToast("info", "You are out of credits. Please purchase more to continue.");
            setShowCreditPlanModal(true); // Open credit purchase modal
            return;
        }
        dispatch(sendMessage(toast, input));
        navigate("/admin/amplitude-ai");
    };

    return (
        <div className="ReleaseAndFinancialPlanner-Page mb-4">
            <ToastContainer />
            <AppTour
                tourSteps={financeHubTourSteps}
                runTour={runTour}
                setRunTour={setRunTour}
            />
            <div className="container max-w-full">
                <div className="mt-4 mb-5" data-tour="amplitude-ai-chat">
                    <ChatInput
                        title={'Release and Financial Planner'}
                        inputPlaceholder={'Use Amplitude in crafting the perfect campaign based on your vision.'}
                        onSend={handleSend}
                        disabled={showLoad}
                    />
                </div>

                {/* 2x2 Grid Section */}
                <div className="forcast-section mb-4 2xl:mb-5">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="grid-item" data-tour="calendar">
                            <div className="info-box h-full bg-theme_clr1 flex flex-col rounded-xl p-4 xl:p-5">
                                <div className="header-section flex flex-wrap items-center justify-between gap-2 mb-[10px] md:mb-[21px]">
                                    <select
                                        value={viewMode}
                                        onChange={(e) => setViewMode(e.target.value)}
                                        className="appearance-none bg-theme_clr1 text-white text-base font-manrope border-0 focus:outline-none pr-8"
                                        style={{
                                            WebkitAppearance: "none",
                                            MozAppearance: "none",
                                            appearance: "none",
                                        }}
                                    >
                                        <option value="month">Month</option>
                                        <option value="year">Year</option>
                                    </select>
                                    <div className="btn-custom3">Your Calendar</div>
                                </div>

                                <Calendar
                                    mode={viewMode}
                                    eventsData={events}
                                    onChange={handleCalendarChange}
                                />
                            </div>
                        </div>
                        <div className="grid-item" data-tour="task-planner">
                            <div className="info-box h-full bg-theme_clr1 flex flex-col rounded-xl p-4 xl:p-5">

                                <div className="wrapper flex flex-col gap-5">
                                    <TaskPlanner initialData={{}} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Forcast Section */}
                <div className="forcast-section bg-theme_clr1 rounded-xl mb-4 2xl:mb-5 p-4 xl:p-6">
                    <div className="header-section flex flex-wrap items-center justify-between gap-3 mb-5">
                        <div className="title-info font-bold font-manrope flex items-center gap-6 text-[23px] text-white leading-tight">
                            <img src={require('../../../assets/images/admin/Amplitude-Info.png')} alt="Amplitude Info" className='w-[30px] h-[30px]' /> Forecast and ROI
                        </div>
                        <button type="button" onClick={() => setRefreshKey(prev => prev + 1)} className="btn-custom3">Refresh <i className="bi bi-arrow-clockwise text-base ms-1"></i></button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-nowrap gap-4 overflow-auto">
                        <ForecastEngine key={refreshKey} {...dspLockProps} />
                        <div className="grid-item lg:w-[30%]" data-tour="revenue-estimator">
                            <RevenueEstimator />
                        </div>
                        <div className="grid-item lg:w-[30%]" data-tour="roi-calculator">
                            <ROICalculator key={refreshKey + '-roi'} {...dspLockProps} />
                        </div>
                    </div>
                </div>

                {/* 2x2 Grid Section */}
                <div className="logs-section">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="grid-item" data-tour="log-revenue">
                            <LogRevenue {...dspLockProps} />
                        </div>
                        <div className="grid-item" data-tour="log-expenses">
                            <LogExpenses />
                        </div>
                    </div>
                </div>
            </div>
            <PricingPlanModal isOpen={showPricingPlanModal} onClose={() => setShowPricingPlanModal(false)} returnUrl={window.location.origin + "/admin/artist-manager/release-and-financial-planner"} />
            <CreditPlanModal isOpen={showCreditPlanModal} onClose={() => setShowCreditPlanModal(false)} />
        </div>
    )
}
